import { Form, Formik, type FormikHelpers, useField, useFormikContext } from "formik";
import { ArrowLeft, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import type { RichTextEditorHandle } from "@/components/common/rich-text-editor";
import { RichTextField } from "@/components/common/rich-text-field";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import { useAuthStore } from "@/features/auth/store/auth-store";
import {
  FIELD_LABEL,
  SettingsSection,
  SettingsStickyBar,
} from "@/features/settings/components/settings-section";
import {
  CRITICAL_TOKENS,
  EMAIL_MERGE_TOKENS,
  type EmailTemplate,
  getEmailTemplate,
  restoreEmailTemplate,
  saveEmailTemplate,
} from "@/features/settings/email-templates-api";
import { applyApiErrors } from "@/lib/form-errors";

const schema = Yup.object({
  subject: Yup.string().trim().required("Subject is required"),
  body: Yup.string().trim().required("Body is required"),
});

interface FormValues {
  subject: string;
  body: string;
}

/** Which field a merge-token button should append to (whichever was last focused). */
type TargetField = "subject" | "body";

function SubjectField({ onFocus }: { onFocus: () => void }) {
  const [field, meta] = useField("subject");
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label htmlFor="subject" className={FIELD_LABEL}>
        Subject
      </Label>
      <Input id="subject" aria-invalid={hasError} {...field} onFocus={onFocus} />
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

function MergeTokenBar({
  target,
  bodyRef,
}: {
  target: TargetField;
  bodyRef: React.RefObject<RichTextEditorHandle | null>;
}) {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  return (
    <div className="flex flex-col gap-2">
      <p className="text-muted-foreground text-xs">
        Insert a merge token (added to the {target} field):
      </p>
      <div className="flex flex-wrap gap-2">
        {EMAIL_MERGE_TOKENS.map((token) => (
          <Button
            key={token}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              if (target === "body") {
                // Rich body: insert at the editor cursor.
                bodyRef.current?.insertText(`{${token}}`);
                return;
              }
              const current = values.subject;
              const next = `${current}${current ? " " : ""}{${token}}`;
              void setFieldValue("subject", next, true);
            }}
          >
            {`{${token}}`}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function EmailTemplateEditPage() {
  const { code = "" } = useParams<{ code: string }>();
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [error, setError] = useState(false);
  const [target, setTarget] = useState<TargetField>("body");
  // Bump to force Formik to re-init after a restore reloads the seed values.
  const formKey = useRef(0);
  const bodyRef = useRef<RichTextEditorHandle>(null);

  useEffect(() => {
    let active = true;
    getEmailTemplate(code)
      .then((t) => active && setTemplate(t))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, [code]);

  const criticalTokens = CRITICAL_TOKENS[code] ?? [];

  function validateCriticalTokens(values: FormValues): Partial<Record<keyof FormValues, string>> {
    const combined = `${values.subject} ${values.body}`;
    const missing = criticalTokens.filter((t) => !combined.includes(`{${t}}`));
    if (missing.length === 0) return {};
    return {
      body: `This template requires the ${missing.map((t) => `{${t}}`).join(", ")} merge token${
        missing.length > 1 ? "s" : ""
      }.`,
    };
  }

  async function handleSubmit(values: FormValues, helpers: FormikHelpers<FormValues>) {
    try {
      const saved = await saveEmailTemplate(code, {
        subject: values.subject.trim(),
        body: values.body,
      });
      setTemplate(saved);
      helpers.resetForm({ values: { subject: saved.subject, body: saved.body } });
      toast.success("Email template saved");
    } catch (err) {
      toast.error(
        applyApiErrors(err, helpers, { fallback: "Couldn't save the email template." }),
      );
    }
  }

  async function handleRestore() {
    try {
      const seed = await restoreEmailTemplate(code);
      setTemplate(seed);
      formKey.current += 1;
      toast.success("Restored to default");
    } catch {
      toast.error("Couldn't restore the template.");
    }
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <p className="text-muted-foreground text-sm">Couldn&apos;t load this email template.</p>
        <Button asChild variant="outline">
          <Link to="/settings/emails">Back to templates</Link>
        </Button>
      </div>
    );
  }

  if (!template) return <Loader className="mt-10" />;

  return (
    <div className="flex flex-col gap-6">
      <Button asChild variant="ghost" size="sm" className="w-fit gap-1.5 pl-2">
        <Link to="/settings/emails">
          <ArrowLeft className="size-4" />
          Back to templates
        </Link>
      </Button>

      <PageHeader
        title={template.subject || template.code}
        subtitle={`${template.groupName} · ${template.code}`}
      />

      {!isAdmin ? (
        <SettingsSection title="Email Template" icon={Mail} bodyClassName="block">
          <div className="grid gap-4">
            <div className="grid gap-1">
              <Label className={FIELD_LABEL}>Subject</Label>
              <p className="text-sm">{template.subject}</p>
            </div>
            <div className="grid gap-1">
              <Label className={FIELD_LABEL}>Body</Label>
              <SanitizedHtml html={template.body} className="text-muted-foreground" />
            </div>
          </div>
        </SettingsSection>
      ) : (
        <Formik<FormValues>
          key={formKey.current}
          initialValues={{ subject: template.subject, body: template.body }}
          validationSchema={schema}
          validate={validateCriticalTokens}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col gap-6">
              <SettingsSection title="Email Template" icon={Mail} bodyClassName="block">
                <div className="grid gap-5">
                  <SubjectField onFocus={() => setTarget("subject")} />
                  <MergeTokenBar target={target} bodyRef={bodyRef} />
                  <RichTextField
                    ref={bodyRef}
                    name="body"
                    label="Body"
                    labelClassName={FIELD_LABEL}
                    onFocus={() => setTarget("body")}
                  />
                </div>
              </SettingsSection>
              <SettingsStickyBar>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="outline">
                      Restore to default
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Restore to default?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This discards your customizations and re-applies the seeded default
                        for this template. This can&apos;t be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRestore}>Restore</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </SettingsStickyBar>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}
